import React, { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ResizeHiddenWrapper from "../../components/Common/ResizeHiddenWrapper";
import { isIphoneX, getPageSameType } from "../../utils/util";
import {
  IComponent,
  IPendantButton as ButtonType,
  IComponentLink,
} from "../../component.d";
import "./Pendant.scss";
import "./Buttons.scss";
import { inject, observer } from "mobx-react";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import ReactSVG from "react-svg";
import RootStore from "../../store/RootStore";
import Mask from "../Mask";
import GotoTopButton from "./GotoTopButton";
import GoodButton from "./GoodButton";
import RefreshButton from "./RefreshButton";
import SettingButton from "./SettingButton";
import LangButton from "./LangButton";
import MoreButton from "./MoreButton";
import FindButton from "./FindButton";
import CustomButton from "./CustomButton";
import OrderButton from "./OrderButton";
import JigsawButton from "./JigsawButton";
import RedpackageButton from "./RedpackageButton";
import ShareButton from "./ShareButton";
import DownloadButton from "./DownloadButton";
import NormalButton from "./NormalButton";
import MobileViewButton from './MobileViewButton';
import useRealBottomTab from "../ComponentNavigator/useRealBottomTab";
import usePortalDom from "../hooks/usePortalDom";
import {PageContext} from "../ComponentNavigatorV2/PageContext";
import activeGalleryInstance from '../GalleryList/ActiveGalleryInstance';

export interface IOneButton {
  show: boolean;
  type: ButtonType;
  image?: string;
  link?: IComponentLink;
  child?: any;
  event?: () => void;
  className?: string;
  hide_setting?: boolean;
}

export interface IAllForPendant extends IComponent {
  buttons?: IOneButton[];
  rootStore?: RootStore;
  useLocalDom?: boolean;
}

const AllForPendant = function (props: IAllForPendant) {
  const { container, rootStore, useLocalDom } = props;
  const { imStore, pagedataStore} = rootStore!;
  const activeGallery = activeGalleryInstance.get();
  const jigsaw = activeGallery ? activeGallery.jigsaw : { bottomShow: false };
  // 如果当前是拼图底部已经显示的情况下，任何挂件都不需要显示
  const { bottomShow } = jigsaw;

  const eXBottom = isIphoneX() ? 20 : 0;
  const [realBottom, setRealBottom] = useState(0);

  const [buttons, setButtons] = useState([] as IOneButton[]);
  const [zIndex, setZIndex] = useState(15);

  const [commentSendShow, setCommentSendShow] = useState(true);
  useEffect(() => {
    const eventHandler = (e: Event) => {
      if (e && e.type == "custom-commentsend-show") {
        setCommentSendShow(true);
      }
      if (e && e.type == "custom-commentsend-hide") {
        setCommentSendShow(false);
      }
    };

    window.addEventListener("custom-commentsend-show", eventHandler);
    window.addEventListener("custom-commentsend-hide", eventHandler);

    return () => {
      window.removeEventListener("custom-commentsend-show", eventHandler);
      window.removeEventListener("custom-commentsend-hide", eventHandler);
    };
  }, [commentSendShow]);

  const resetButtons = () => {
    // 单一数据源：是否展示拼图/海报入口由 pageDetail.template 里的 pendent.buttons 决定，
    // 前端不再为展商页自动补按钮，避免未来开关功能时需要改两边。
    setButtons([...(props.buttons || [])] as IOneButton[]);
  };

  useEffect(() => {
    resetButtons();
  }, []);

  // 检测一下组件变更提醒
  useEffect(()=>{
    const onComponentChange = (data:any)=>{
      if(data) {
        const {link_id, component} = data;
        if(component && component.cid && link_id == window.pageDetail.link_id) {
          // 同一个界面，才刷新一下
          // 如果是挂件的话，还需要单独处理一下
          if(component && component.type == 'pendent' && component.buttons) {
            // 不处理，交给具体的地方
            if(props.cid == component.cid) {
              setButtons([
                ...(component.buttons as IOneButton[])
              ]);
            }
          }
        }
      }
    };

    imStore.addMessageListener('component_refresh', onComponentChange);

    return ()=>{
      imStore.removeMessageListener('component_refresh', onComponentChange);
    };
  }, [buttons]);

  useEffect(() => {
    const notifyButtonsChange = (e: any) => {
      let eButtons = (e.buttons || []) as IOneButton[];
      let action = (e.action || "") as string;
      if (action == "event") {
        setButtons(
          eButtons.map((btn: any) => {
            return {
              type: btn.eventType || "event",
              show: true,
              child: btn.child,
              event: btn.event,
            } as IOneButton;
          })
        );
      }

      if (action == "reset") {
        // setButtons(props.buttons);
        resetButtons();
        setZIndex(3);
      }

      if (action == "zindex") {
        setZIndex(Number(e.data));
      }
    };

    window.addEventListener("PENDENT_CHANGE", notifyButtonsChange);

    return () => {
      window.removeEventListener("PENDENT_CHANGE", notifyButtonsChange);
    };
  }, [buttons]);

  useEffect(() => {
    let { template } = window.pageDetail;
    let paddingBottom = isIphoneX() ? 22 : 0;
    let shouldBorder = false;

    const { showNavigator } = useRealBottomTab();
    // if (showNavigator) {
    //   paddingBottom += 49;
    //   shouldBorder = true;
    // }
    const commentsend = getPageSameType(template, [], ["commentsend"]);
    if (commentsend && commentsend.length && commentSendShow) {
      paddingBottom += 66;
    }

    if (shouldBorder) {
      paddingBottom += 15;
    }

    // paddingBottom -= 15;

    setRealBottom(Math.max(15, paddingBottom));
  }, [commentSendShow]);

  const { right = 0, bottom = 0 } = container || {};

  let wrapper = Object.assign(
    { right, bottom: +realBottom },
    bottomShow
      ? {
          visibility: "hidden",
          pointerEvents: "none",
        }
      : {}
  );

  // 展示区和收纳区
  const [showButtons, setShowButtons] = useState([] as IOneButton[]);
  const [foldButtons, setFoldButtons] = useState([] as IOneButton[]);

  useEffect(() => {
    // console.log(`rebuild showbutton and foldbutton`);
    setShowButtons(buttons.filter((it: IOneButton) => it.show));
    setFoldButtons(buttons.filter((it: IOneButton) => !it.show));
  }, [buttons]);

  const renderButton = (item: IOneButton, index: number, isFold: boolean) => {
    let { type } = item;
    // console.log(`re-render button`, type, item);
    switch (type) {
      case "download":
        return <DownloadButton key={type + index} />;
      case "good":
        return <GoodButton key={type + index} />;
      case "refresh":
        return <RefreshButton rootStore={rootStore} key={type + index} />;
      case "setting":
        return (
          <SettingButton
            rootStore={rootStore}
            key={type + index}
            isFold={isFold}
            settingMask={onSettingMask}
          />
        );
      case "lang":
        return <LangButton rootStore={rootStore} key={type + index} />;
      case "people":
        return <FindButton rootStore={rootStore} key={type + index} />;
      case "event":
        return (
          <NormalButton onClick={item.event} key={type + index} className={item.className}>
            {item.child}
          </NormalButton>
        );
      case "custom":
        return <CustomButton key={type + index} {...item} />;
      case "order":
        return <OrderButton key={type + index} />;
      case "jigsaw":
        return <JigsawButton rootStore={rootStore} key={type + index} isFold={isFold} />;
      case "redpackage":
        return <RedpackageButton rootStore={rootStore} key={type + index} />;
      case "share":
        return (
          <ShareButton
            rootStore={rootStore}
            key={type + index}
            isFold={isFold}
          />
        );
    }

    console.log("unknown pendant", type);
  };

  const [showMore, setShowMore] = useState(false);
  // 为了修复在more展开后，如果有下载、拼图等，点击后没有干掉Mask并还原Expand状态的问题
  // expand从More组件移动到这里来控制
  const [moreBtnExpand, setMoreBtnExpand] = useState(false); // more按钮是否展开状态
  const showFoldButtons = () => {
    setShowMore(true);
  };

  const hideFoldButtons = () => {
    setShowMore(false);
  };

  const context = useContext(PageContext);

  const moreButtonRef = usePortalDom({
    targetClassName:"pendant-more-portal",
    parentElement:window.document.getElementById(`page-bottom-${context.cid}`)! as HTMLDivElement
  });

  useEffect(() => {
    const hideMoreButton = (e: Event) => {
      setShowMore(false);
      if (e.data) {
        setMoreBtnExpand(false);
      }
    };
    if (showMore) {
      window.addEventListener("PENDENT_FOLD", hideMoreButton);
    }

    return () => {
      window.removeEventListener("PENDENT_FOLD", hideMoreButton);
    };
  }, [showMore]);

  const switchButtons = () => {
    if (showMore) {
      return [
        // moreButtonRef ? createPortal(
        //   <>
        //     <Mask />
        //   </>,
        //   moreButtonRef! as HTMLElement
        // ) : null,
        foldButtons.map((it: IOneButton, index: number) => {
          return renderButton(it, index, true);
        }),
      ];
    } else {
      return showButtons.map((it: IOneButton, index: number) => {
        return renderButton(it, index, false);
      });
    }
  };

  // const renderMethod = pagedataStore.renderMode == 'pc' ? (child:any)=>{
  //   return child;
  // } : (child:any, domContainer:HTMLElement | null)=>{
  //   return domContainer ? createPortal(child, domContainer) : null;
  // };
  const renderMethod = (child:any, domContainer?:HTMLDivElement)=>{
    return child;
  };

  const checkAndRenderButton = (type:string, index:number, isFold:boolean) => {
    let button = null as any;

    if(type == 'lucky') {
      button = buttons.find((it:any)=>{
        if(it && it.type == 'custom' && it.link && it.link.type == 'show_lucky') {
          return true;
        }
        return false;
      });

      if(button) {
        button.children = <>
          <ReactSVG
            src={require("../../svg/luck.pc.svg")}
            style={{ width: 9, height: 14, margin: "0 auto" }}
          />
          <span>抽奖</span>
        </>;
      }
    }
    if(type == 'good') {
      button = buttons.find((it:any)=>{
        if(it && it.type == 'good') {
          return true;
        }
        return false;
      });
    }
    if(type == 'lang') {
      button = buttons.find((it:any)=>{
        if(it && it.type == 'lang') {
          return true;
        }
        return false;
      })
    }

    if(button) {
      return renderButton(button, index, isFold);
    }

    return null;
  };

  const [maskOption, setMaskOption] = useState({
    show:false,
    callback:null
  } as {
    show:boolean,
    callback?:any
  });

  const onSettingMask = (status:boolean, callback?:any)=>{
    setMaskOption({
      show:status,
      callback:callback
    });
  };

  useEffect(()=>{
    setMaskOption({
      show:moreBtnExpand
    });
  }, [moreBtnExpand]);
  // console.log(maskOption);
  const instance = <>
    {maskOption && maskOption.show ? <Mask onClick={()=>{
      // if(maskOption.callback) {
      //   maskOption.callback();
      // }else {
      //   hideFoldButtons();
      // }
      hideFoldButtons();
      setMoreBtnExpand(false);
    }} /> : null}
    <div
      className="pendant"
      // @ts-ignore
      style={{
        ...wrapper,
        zIndex,
      }}
    >
      {pagedataStore.renderMode == 'pc' ? <>
        <div className="pendant-row-column">
          <MobileViewButton />
          {checkAndRenderButton('lucky', 1, false)}
        </div>
        <div className="pendant-row-column">
          {checkAndRenderButton('good', 2, false)}
          {checkAndRenderButton('lang', 3, false)}
        </div>
      </> : <>
        <GotoTopButton />
        {switchButtons()}
        {foldButtons.length ? (
          <MoreButton
            onShow={showFoldButtons}
            onHide={hideFoldButtons}
            expand={moreBtnExpand}
            setExpand={setMoreBtnExpand}
          />
        ) : null}
      </>}
    </div>
  </>;

  return moreButtonRef && !useLocalDom ? createPortal(
    instance, moreButtonRef
  ) : instance;
};

export default inject("rootStore")(observer(AllForPendant));
